import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import {
  CalendarTimeSlot,
  formatMinutes,
} from '@techspert-io/schedule-calendar';
import moment from 'moment-timezone';
import { IBestMatch } from './expert-availability-best-matches.utils';

/** The best times to select, as tiles naming how preferred each side is */
@Component({
  selector: 'app-expert-availability-best-matches',
  templateUrl: './expert-availability-best-matches.component.html',
  styleUrl: './expert-availability-best-matches.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule],
})
export class ExpertAvailabilityBestMatchesComponent {
  readonly matches = input.required<IBestMatch[]>();
  /** Names the dates the matches are from, such as 'this week' */
  readonly period = input.required<string>();
  readonly expertName = input.required<string>();
  /** Whether the expert has any times on the dates, matching or not */
  readonly hasExpertTimes = input.required<boolean>();
  /**
   * Matches can't be selected while disabled, but stay focusable so focus can
   * come back to the one selected
   */
  readonly disabled = input(false);

  readonly selectTime = output<CalendarTimeSlot>();

  readonly emptyText = computed(() => {
    const name = this.expertName();

    return this.hasExpertTimes()
      ? `No matching times ${this.period()}. You can still select a time from ${name}'s availability.`
      : `${name.charAt(0).toUpperCase()}${name.slice(1)} is not available ${this.period()}.`;
  });

  readonly tiles = computed(() =>
    this.matches().map(({ slot, isExpertPreferred, isClientPreferred }) => ({
      slot,
      date: moment(slot.date).format('ddd D MMM'),
      time: `${formatMinutes(slot.start)} – ${formatMinutes(slot.end)}`,
      expert: isExpertPreferred
        ? { icon: 'star', label: 'Expert preferred' }
        : { icon: 'event_available', label: 'Expert available' },
      client: isClientPreferred
        ? { icon: 'star', label: 'You prefer' }
        : { icon: 'event_available', label: 'You are available' },
    }))
  );

  select(slot: CalendarTimeSlot) {
    if (!this.disabled()) {
      this.selectTime.emit(slot);
    }
  }
}
